import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Calendar, Check, Clock, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";

type TodoTask = Tables<"todo_tasks">;

const priorityColors = {
  high: "border-destructive bg-destructive/10 text-destructive",
  medium: "border-warning bg-warning/10 text-warning",
  low: "border-muted bg-muted text-muted-foreground",
};

const statusColors = {
  pending: "bg-muted text-muted-foreground",
  in_progress: "bg-warning text-warning-foreground",
  completed: "bg-success text-success-foreground",
};

export const TaskManager = () => {
  const [tasks, setTasks] = useState<TodoTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_at: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("todo_tasks")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load tasks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (!newTask.title.trim()) {
        toast({
          title: "Title required",
          description: "Please enter a task title",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from("todo_tasks").insert({
        user_id: user.id,
        title: newTask.title.trim(),
        description: newTask.description.trim() || null,
        priority: newTask.priority,
        due_at: newTask.due_at || null,
        status: "pending",
      });

      if (error) throw error;

      toast({
        title: "Task created",
        description: "New task has been added",
      });

      setNewTask({ title: "", description: "", priority: "medium", due_at: "" });
      setShowCreateForm(false);
      fetchTasks();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      });
    }
  };

  const handleToggleComplete = async (task: TodoTask) => {
    try {
      const newStatus = task.status === "completed" ? "pending" : "completed";
      const { error } = await supabase
        .from("todo_tasks")
        .update({
          status: newStatus,
          completed_at: newStatus === "completed" ? new Date().toISOString() : null,
        })
        .eq("id", task.id);

      if (error) throw error;
      fetchTasks();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    }
  };

  const completedCount = tasks.filter((t) => t.status === "completed").length;
  const totalCount = tasks.length;
  const completionRate = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (loading) {
    return <Card className="p-6">Loading tasks...</Card>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Button onClick={() => setShowCreateForm(!showCreateForm)} className="gradient-primary">
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      {showCreateForm && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Create New Task</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Task title"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Add details..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={newTask.priority}
                  onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="datetime-local"
                  value={newTask.due_at}
                  onChange={(e) => setNewTask({ ...newTask, due_at: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCreateTask} className="gradient-primary">
                Create Task
              </Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Progress Overview */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Task Completion</span>
          <span className="text-sm text-muted-foreground">
            {completedCount} of {totalCount} completed
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-success h-2 rounded-full transition-base"
            style={{ width: `${completionRate}%` }}
          />
        </div>
      </Card>

      {/* Tasks List */}
      <div className="space-y-3">
        {tasks.map((task) => (
          <Card
            key={task.id}
            className={cn(
              "p-4 transition-base hover:shadow-md",
              task.status === "completed" && "opacity-60"
            )}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                checked={task.status === "completed"}
                onCheckedChange={() => handleToggleComplete(task)}
                className="mt-1"
              />

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h4
                    className={cn(
                      "font-medium",
                      task.status === "completed" && "line-through text-muted-foreground"
                    )}
                  >
                    {task.title}
                  </h4>
                  <div className="flex gap-2">
                    <Badge className={statusColors[task.status as keyof typeof statusColors] || statusColors.pending}>
                      {task.status}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={priorityColors[task.priority as keyof typeof priorityColors] || priorityColors.medium}
                    >
                      {task.priority}
                    </Badge>
                  </div>
                </div>

                {task.description && (
                  <p className="text-justify text-sm text-muted-foreground mb-2">{task.description}</p>
                )}

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {task.due_at && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Due {new Date(task.due_at).toLocaleDateString()}
                    </div>
                  )}
                  {task.linked_entity_type && (
                    <div className="flex items-center gap-1">
                      Linked to {task.linked_entity_type}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}

        {tasks.length === 0 && (
          <Card className="p-8 text-center">
            <p className="text-justify text-muted-foreground">No tasks yet. Create your first task to get started!</p>
          </Card>
        )}
      </div>
    </div>
  );
};
